import clsx from "clsx";
import { ReactElement } from "react";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";

import { MessageAttachmentCard } from "./MessageAttachmentCard";

export interface MathChatBotMessageProps {
  messageHtml: string;
  timeStamp: string;
  isBotResponse: boolean;
  attachment?: {
    fileName: string;
    fileSize: number;
    fileType: string;
    uploadID?: number;
  };
}

function normalizeMathMarkdown(input: string): string {
  return (input || "")
    // Convert common LLM display-math delimiters to remark-math delimiters.
    .replace(/\\\[/g, "$$")
    .replace(/\\\]/g, "$$")
    .replace(/\\\(/g, "$ ")
    .replace(/\\\)/g, " $");
}

export function MathChatBotMessage({
  messageHtml,
  timeStamp,
  isBotResponse,
  attachment,
}: MathChatBotMessageProps): ReactElement {
  const messageClasses = clsx(
    !isBotResponse && "justify-content-end",
    "d-flex align-items-baseline mb-2 message-wrapper"
  );

  const renderedMessage = isBotResponse ? normalizeMathMarkdown(messageHtml) : messageHtml;

  return (
    <div className={messageClasses}>
      <div style={{ maxWidth: "85%" }}>
        <div
          className={clsx(
            isBotResponse && "bg-secondary",
            !isBotResponse && "enlive-gradient",
            "card display-inline-block p-3 my-2 bg-inherit markdown-content math-markdown-content"
          )}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[rehypeKatex]}
            components={{
              a({ href, children, ...props }: any) {
                if (href === "#cursor") {
                  return <span className="cursor-blink" aria-hidden="true" />;
                }
                return (
                  <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                    {children}
                  </a>
                );
              },
              table({ children }) {
                return (
                  <div className="markdown-table-wrapper">
                    <table>{children}</table>
                  </div>
                );
              },
              code({ inline, className, children, ...props }: any) {
                const match = /language-(\w+)/.exec(className || "");

                if (!inline && match) {
                  return (
                    <SyntaxHighlighter
                      style={oneDark}
                      language={match[1]}
                      PreTag="div"
                      customStyle={{
                        borderRadius: "0.5rem",
                        fontSize: "0.85em",
                        margin: "0.5em 0 0.75em 0",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                      }}
                      {...props}
                    >
                      {String(children).replace(/\n$/, "")}
                    </SyntaxHighlighter>
                  );
                }

                if (!inline && !match) {
                  return <div className="markdown-text-chip">{String(children).replace(/\n$/, "")}</div>;
                }

                return (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {renderedMessage}
          </ReactMarkdown>

          {attachment && (
            <MessageAttachmentCard
              fileName={attachment.fileName}
              fileSize={attachment.fileSize}
              fileType={attachment.fileType}
              uploadID={attachment.uploadID}
            />
          )}
        </div>
        <div className={clsx(!isBotResponse && "text-end", "small px-2")}>{timeStamp}</div>
      </div>
    </div>
  );
}

export default MathChatBotMessage;